import { useMutation, useQuery } from '@apollo/client';
import { ColumnsType } from 'antd/es/table';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Footer, TitleBar } from '../../../../../components';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import {
  DocumentLinks,
  OwnersInformation,
  SummaryContent,
} from '../../components';

import {
  CREATE_INBOUND_MATERIAL,
  CREATE_OUTBOUND_MATERIAL,
  DELETE_INBOUND_MATERIAL,
  DELETE_OUTBOUND_MATERIAL,
  FIND_ALL_INBOUND_MATERIALS_BY_SUPPLY_CHAIN,
  FIND_ALL_OUTBOUND_MATERIALS_BY_SUPPLY_CHAIN,
  FIND_SUPPLY_CHAIN_BY_STRATEGY,
  GET_SECTION_OWNER,
  UPDATE_INBOUND_MATERIAL,
  UPDATE_OUTBOUND_MATERIAL,
  UPDATE_SECTION_OWNER,
  UPDATE_SUPPLY_CHAIN,
} from '../../../queries';
import { GET_DOCUMENT_LINKS_BY_SUPPLY_CHAIN } from '../../../queries/documentLink';
import { STRATEGY_MODELS, STRATEGY_TYPES } from '../constants';
import { formFieldsInbound } from './formFieldsInbound';
import { formFieldsOutbound } from './formFieldsOutbound';
import { tableSortingMethods } from '../../../../../utils/tableSortingMethods';
import { cleanUpObject } from '../../../../../utils';
import { ExportColumn } from '../../../../../interfaces/ExportColumn';
import { baseColumnsInbound, baseColumnsOutbound } from './exportColumns';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type StrategyFacilityProps = {
  strategyType?: string;
};

const SupplyChainPA = ({
  strategyType = 'processArea',
}: StrategyFacilityProps) => {
  const { locationId, processAreaId, strategyId } = useParams();
  const [searchParams] = useSearchParams();
  const locationName = searchParams.get('locationName');
  const strategyLength = searchParams.get('strategyLength');

  let strategyName;

  if (strategyType === 'processArea') {
    strategyName = searchParams.get('name');
  }

  const subtitle =
    strategyType === 'processArea'
      ? `${locationName} // ${strategyName} // ${strategyLength} Month Strategy`
      : `${locationName} // ${strategyLength} Month Strategy`;

  const backPath =
    strategyType === 'processArea'
      ? `/process-areas-and-strategies/${locationId}/${processAreaId}`
      : `/strategy-summary/${locationId}/${strategyId}?locationName=${locationName}&strategyLength=${strategyLength}`;

  const OPERATION_NAMES = {
    SECTION_OWNER: 'GetSectionOwnerByStrategy',
    INBOUND: 'FindAllInboundMaterialsBySupplyChain',
    OUTBOUND: 'FindAllOutboundMaterialsBySupplyChain',
    STRATEGY_SUPPLY_CHAIN: 'FindSupplyChainByStrategy',
    DOCUMENTS: 'FindAllBySupplyChain',
  };

  const {
    data: strategySupplyChain,
    loading: strategySupplyChainLoading,
    error: strategySupplyChainError,
  } = useQuery(FIND_SUPPLY_CHAIN_BY_STRATEGY, {
    variables: {
      uuid: strategyId,
    },
    skip: !strategyId,
  });

  const strategySupplyChainData =
    strategySupplyChain?.findSupplyChainByStrategy;

  const { data: strategyOwner, error: strategyOwnerError } = useQuery(
    GET_SECTION_OWNER,
    {
      variables: {
        uuid: strategyId,
        strategyType: STRATEGY_TYPES.SUPPLY_CHAIN,
      },
      skip: !strategyId,
    },
  );

  const sectionOwner = strategyOwner?.findSectionOwnerByStrategy;

  const {
    data: inbound,
    loading: inboundLoading,
    error: inboundError,
  } = useQuery(FIND_ALL_INBOUND_MATERIALS_BY_SUPPLY_CHAIN, {
    variables: {
      uuid: strategySupplyChainData?.uuid,
    },
    skip: !strategySupplyChainData?.uuid,
  });

  const inboundData = inbound?.findAllInboundMaterialsBySupplyChain;

  const {
    data: outbound,
    loading: outboundLoading,
    error: outboundError,
  } = useQuery(FIND_ALL_OUTBOUND_MATERIALS_BY_SUPPLY_CHAIN, {
    variables: {
      uuid: strategySupplyChainData?.uuid,
    },
    skip: !strategySupplyChainData?.uuid,
  });

  const outboundData = outbound?.findAllOutboundMaterialsBySupplyChain;

  const summaryData = {
    uuid: strategySupplyChainData?.uuid,
    summary: strategySupplyChainData?.inBoundSummary,
  };

  const { data: documents, error: documentsError } = useQuery(
    GET_DOCUMENT_LINKS_BY_SUPPLY_CHAIN,
    {
      variables: {
        uuid: strategySupplyChainData?.uuid,
      },
      skip: !strategySupplyChainData?.uuid,
    },
  );

  const documentsData = documents?.findAllBySupplyChain;

  const [updateSectionOwner] = useMutation(UPDATE_SECTION_OWNER, {
    refetchQueries: [OPERATION_NAMES.SECTION_OWNER],
  });

  const [createInboundMaterial] = useMutation(CREATE_INBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.INBOUND],
  });

  const [updateInboundMaterial] = useMutation(UPDATE_INBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.INBOUND],
  });

  const [deleteInboundMaterial] = useMutation(DELETE_INBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.INBOUND],
  });

  const [createOutboundMaterial] = useMutation(CREATE_OUTBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.OUTBOUND],
  });

  const [updateOutboundMaterial] = useMutation(UPDATE_OUTBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.OUTBOUND],
  });

  const [deleteOutboundMaterial] = useMutation(DELETE_OUTBOUND_MATERIAL, {
    refetchQueries: [OPERATION_NAMES.OUTBOUND],
  });

  const [updateStrategySupplyChain] = useMutation(UPDATE_SUPPLY_CHAIN, {
    refetchQueries: [OPERATION_NAMES.STRATEGY_SUPPLY_CHAIN],
  });

  const inboundColumns: ColumnsType<any> = [
    {
      key: 'materialTypeName',
      title: 'Material/Type',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.materialTypeName, b.materialTypeName),
      render: ({ materialTypeName }) => (
        <span className="custom-table--column custom-table--column-primary">
          {materialTypeName}
        </span>
      ),
    },
    {
      key: 'supplierName',
      title: 'Supplier Name',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.supplierName, b.supplierName),
      render: ({ supplierName }) =>
        supplierName ? (
          <span className="custom-table--column">{supplierName}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'newDestination',
      title: 'New Destination',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.newDestination, b.newDestination),
      render: ({ newDestination }) =>
        newDestination ? (
          <span className="custom-table--column">{newDestination}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'shippingMethod',
      title: 'Shipping Method',
      width: 100,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.string(a.shippingMethod, b.shippingMethod),
      render: ({ shippingMethod }) =>
        shippingMethod ? (
          <span className="custom-table--column">{shippingMethod}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.internalContactFirstName,
          b.internalContactFirstName,
        ),
      render: ({
        internalContactFirstName,
        internalContactLastName,
        internalEmail,
      }) =>
        internalContactFirstName || internalContactLastName || internalEmail ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {internalContactFirstName} {internalContactLastName}
            </span>
            <span>{internalEmail}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const outboundColumns: ColumnsType<any> = [
    {
      key: 'materialTypeName',
      title: 'Product/Material',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.materialTypeName, b.materialTypeName),
      render: ({ materialTypeName }) => (
        <span className="custom-table--column custom-table--column-primary">
          {materialTypeName}
        </span>
      ),
    },
    {
      key: 'commentary',
      title: 'Commentary',
      width: 250,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.commentary, b.commentary),
      render: ({ commentary }) =>
        commentary ? (
          <span className="custom-table--column">{commentary}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'internalContact',
      title: 'Internal Contact',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.firstName, b.firstName),
      render: ({ firstName, lastName, email }) =>
        firstName || lastName || email ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>
              {firstName} {lastName}
            </span>
            <span>{email}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariablesInbound = ({ values, type }: ModifyVariablesArgs) => {
    const {
      sectionOwnerfirstName,
      sectionOwnerlastName,
      email,
      ...restValues
    } = values;

    const fields = {
      ...restValues,
      internalContactFirstName: sectionOwnerfirstName,
      internalContactLastName: sectionOwnerlastName,
      internalEmail: email,
    };

    const cleanedValues = cleanUpObject(fields);

    if (type === 'create') {
      return {
        ...cleanedValues,
        supplyChain: {
          uuid: strategySupplyChainData?.uuid,
        },
      };
    }

    return {
      ...cleanedValues,
    };
  };

  const modifyVariablesOutbound = ({ values, type }: ModifyVariablesArgs) => {
    const {
      sectionOwnerfirstName,
      sectionOwnerlastName,
      email,
      ...restValues
    } = values;

    const commonFields = {
      commentary: restValues?.commentary ? restValues?.commentary : null,
    };

    const fields = {
      firstName: sectionOwnerfirstName,
      lastName: sectionOwnerlastName,
      email: email,
      ...restValues,
      ...commonFields,
    };

    const cleanedValues = cleanUpObject(fields);
    if (type === 'create') {
      return {
        ...cleanedValues,
        supplyChain: {
          uuid: strategySupplyChainData?.uuid,
        },
      };
    }

    return {
      ...cleanedValues,
    };
  };

  const modifySummaryVariables = (summary: string) => {
    return {
      inBoundSummary: summary,
    };
  };

  return (
    <>
      <TitleUpdater title={`Supply Chain`} />
      <TitleBar title="BCP Hub" subtitle={subtitle} />
      <section className="flex flex-col gap-4">
        <OwnersInformation
          title="Supply Chain Section Owner"
          data={sectionOwner}
          updateGQL={updateSectionOwner}
          error={strategyOwnerError}
        />
        <QueryErrorBoundary error={inboundError}>
          <TableV3
            title="Inbound Material That Needs to Be Re-Routed"
            columns={inboundColumns}
            data={inboundData}
            loading={inboundLoading}
            editable
            formFields={formFieldsInbound}
            createGQL={createInboundMaterial}
            updateGQL={updateInboundMaterial}
            deleteGQL={deleteInboundMaterial}
            modifyVariables={modifyVariablesInbound}
            exportExcelColumns={baseColumnsInbound}
          />
        </QueryErrorBoundary>
        <QueryErrorBoundary error={outboundError}>
          <TableV3
            title="Special Routing Requirements for Outbound Product or Material"
            columns={outboundColumns}
            data={outboundData}
            loading={outboundLoading}
            editable
            formFields={formFieldsOutbound}
            createGQL={createOutboundMaterial}
            updateGQL={updateOutboundMaterial}
            deleteGQL={deleteOutboundMaterial}
            modifyVariables={modifyVariablesOutbound}
            exportExcelColumns={baseColumnsOutbound}
          />
        </QueryErrorBoundary>
        <section className="w-full grid xl:grid-cols-2 gap-4">
          <SummaryContent
            title="Supply Chain Commentary"
            data={summaryData}
            loading={strategySupplyChainLoading}
            error={strategySupplyChainError}
            updateGQL={updateStrategySupplyChain}
            modifyVariables={modifySummaryVariables}
          />
          <DocumentLinks
            data={documentsData}
            error={documentsError}
            parentUuid={strategySupplyChainData?.uuid}
            queryToUpdate={OPERATION_NAMES.DOCUMENTS}
            model={STRATEGY_MODELS.SUPPLY_CHAIN}
          />
        </section>
        <Footer backPath={backPath} />
      </section>
    </>
  );
};

export default SupplyChainPA;
